// Human-readable labels are intentionally separate from protocol values.
const PHASE_COPY = {
  reset: ['Restarting activity', 'Wind conditions and turbine motion are returning to their initial state.'],
  calm: ['Turbine stopped', 'The rotor is stopped and the station is ready.'],
  steady: ['Steady wind', 'Steady wind is available. The turbine is waiting to align.'],
  aligning: ['Aligning to wind', 'The nacelle is turning toward the wind.'],
  realigning: ['Wind direction changed', 'The nacelle is turning to face the wind again.'],
  generating: ['Generating', 'The turbine is aligned and the rotor is running.'],
  highWind: ['High-wind shutdown', 'The rotor is stopped and the runway alert is active.'],
};

const NOTICE_COPY = {
  unsafeGenerator: 'Generation is on when the current conditions are unsafe.',
  unsafeInactive: 'The turbine became inactive before its movement finished.',
  unsafeAlign: 'Alignment is on when the turbine cannot safely turn.',
  missingRunwayAlert: 'High wind is present, but the runway alert is off.',
  unexpectedRunwayAlert: 'The runway alert is on without a high-wind condition.',
};

function titleCase(value) {
  return value.replace(/([A-Z])/g, ' $1').replace(/^./, (letter) => letter.toUpperCase());
}

function describeState(state) {
  // A phase describes the plant, not a guarantee that the student's controller
  // is correct. Use the reported motion and outputs, including during grace time.
  if (state.phase === 'highWind') {
    const rotor = state.rotorBand === 'stopped' ? 'The rotor is stopped' : 'The rotor is slowing to a stop';
    return `${rotor} and the runway alert is ${state.runwayWindAlert ? 'active' : 'off'}.`;
  }
  if (state.phase === 'calm' && state.rotorBand !== 'stopped') return 'Wind is calm. The rotor is slowing to a stop.';
  if (state.phase === 'realigning' && !(state.align && state.active)) return 'The wind direction changed. The turbine is waiting to turn.';
  if (state.phase === 'steady' && state.aligned) {
    return state.rotorBand === 'decelerating'
      ? 'The turbine is aligned and the rotor is slowing to a stop.'
      : 'The turbine is aligned and the rotor is stopped.';
  }
  if (state.phase === 'generating' && state.rotorBand === 'accelerating') return 'The turbine is aligned and the rotor is speeding up.';
  return PHASE_COPY[state.phase][1];
}

export class WindStationUI {
  constructor({ onScenario, onRestart, onReducedMotion }) {
    this.app = document.querySelector('#app');
    this.connectionLabel = document.querySelector('#connection-label');
    this.scenarioButtons = [...document.querySelectorAll('[data-scenario]')];
    this.restartButton = document.querySelector('#restart-action');
    this.motionButton = document.querySelector('#motion-toggle');
    this.notice = document.querySelector('#notice');
    this.loading = document.querySelector('#loading-state');
    this.liveRegion = document.querySelector('#live-region');
    this.details = document.querySelector('#signal-details');
    this.previousPhase = null;
    this.previousPhaseNote = null;
    this.previousNotice = 'none';

    this.scenarioButtons.forEach((button) => {
      button.addEventListener('click', () => onScenario(button.dataset.scenario));
    });
    this.restartButton.addEventListener('click', onRestart);

    const prefersReduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    this.motionButton.setAttribute('aria-pressed', String(prefersReduced));
    this.motionButton.textContent = prefersReduced ? 'Use full motion' : 'Reduce motion';
    this.motionButton.addEventListener('click', () => {
      const reduced = this.motionButton.getAttribute('aria-pressed') !== 'true';
      this.motionButton.setAttribute('aria-pressed', String(reduced));
      this.motionButton.textContent = reduced ? 'Use full motion' : 'Reduce motion';
      onReducedMotion(reduced);
    });
    onReducedMotion(prefersReduced);
  }

  setConnection(status) {
    // This label is a live region; leave it untouched on unchanged heartbeats.
    if (this.app.dataset.connection === status) return;
    this.app.dataset.connection = status;
    this.connectionLabel.textContent = titleCase(status);
  }

  setPending(pending) {
    [...this.scenarioButtons, this.restartButton].forEach((button) => {
      button.disabled = pending;
    });
  }

  setSceneReady(error) {
    if (error) {
      this.loading.hidden = false;
      // A heartbeat must not erase the persistent loading/rendering error.
      this.loading.textContent = error;
      return;
    }
    this.loading.hidden = true;
  }

  render(state) {
    // Like the scene, the DOM is rebuilt from each complete report snapshot.
    this.app.dataset.phase = state.phase;
    const phaseLabel = state.phase === 'calm' && state.rotorBand !== 'stopped'
      ? 'Calm wind'
      : PHASE_COPY[state.phase][0];
    const phaseNote = describeState(state);
    this.setField('phaseLabel', phaseLabel);
    this.setField('phaseNote', phaseNote);
    this.setField('windBand', titleCase(state.windBand));
    this.setField('windDirection', state.windDirection);
    this.setField('aligned', state.aligned ? 'Yes' : 'No');
    this.setField('rotorBand', titleCase(state.rotorBand));

    for (const key of ['active', 'align', 'generatorEnable', 'runwayWindAlert']) {
      const element = document.querySelector(`[data-output="${key}"]`);
      const value = state[key] ? '1' : '0';
      element.textContent = value;
      element.dataset.value = value;
    }

    this.scenarioButtons.forEach((button) => {
      if (button.dataset.scenario === state.scenario) button.setAttribute('aria-current', 'true');
      else button.removeAttribute('aria-current');
    });

    this.renderDetails(state);
    const noticeText = NOTICE_COPY[state.notice];
    this.notice.hidden = !noticeText;
    this.notice.textContent = noticeText ?? '';

    // Announce semantic changes, not every heartbeat, to avoid screen-reader spam.
    if (state.phase !== this.previousPhase || phaseNote !== this.previousPhaseNote) {
      this.announce(`${phaseLabel}. ${phaseNote}`);
      this.previousPhase = state.phase;
      this.previousPhaseNote = phaseNote;
    } else if (state.notice !== 'none' && state.notice !== this.previousNotice) {
      this.announce(noticeText);
    }
    this.previousNotice = state.notice;
  }

  setField(name, value) {
    const element = document.querySelector(`[data-field="${name}"]`);
    if (element) element.textContent = String(value);
  }

  renderDetails(state) {
    this.details.replaceChildren();
    for (const [key, rawValue] of Object.entries(state)) {
      const row = document.createElement('tr');
      const heading = document.createElement('th');
      const value = document.createElement('td');
      heading.scope = 'row';
      heading.textContent = key;
      value.textContent = typeof rawValue === 'boolean' ? (rawValue ? '1' : '0') : String(rawValue);
      row.append(heading, value);
      this.details.append(row);
    }
  }

  announce(message) {
    this.liveRegion.textContent = '';
    window.setTimeout(() => { this.liveRegion.textContent = message; }, 20);
  }
}
